// components/CallDetailsModal.jsx
import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Grid,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { formatDateTimeJSX } from '../utils/dateFormatter';

import {formatDateTimeJSXModal} from '../utils/dateFormatterModal';
import { exportModalDataToCSV } from '../utils/csvExporter';
import { useReportStyles } from '../styles/reportStyles';

export const CallDetailsModal = ({
  open,
  onClose,
  selectedRow,
  buyerDialogs,
  audioControls,
}) => {
  const classes = useReportStyles();

  if (!selectedRow) return null;

  const handleExport = () => {
    const filename = `call_details_${selectedRow.caller_id_number}_${new Date().toISOString().slice(0,10)}.csv`;
    exportModalDataToCSV(selectedRow, buyerDialogs || [], filename);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          p: { xs: "10px 12px", sm: "2px 20px" },
        }}
      >
        {/* Title */}
        <Typography
          className="extension_title"
          sx={{
            color: "#0a140e",
            fontSize: { xs: 18, sm: 20 },
            fontWeight: 600,
            textAlign: { xs: "center", sm: "left" },
            width: "100%",
          }}
        >
          Call Details
        </Typography>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "space-between", sm: "flex-end" },
            alignItems: "center",
            width: { xs: "100%", sm: "auto" },
            gap: 1,
          }}
        >
          <Button
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            variant="outlined"
            size="small"
            sx={{ mr: { sm: 2 }, width: { xs: "100%", sm: "100%" } }}
            style={{
              background: "#0a140e",
              color: "#fff",
              textTransform: "capitalize",
              width: "130px",
            }}
          >
            Export CSV
          </Button>

          <IconButton onClick={onClose} className="close_icon">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              sx={{
                fontWeight: "400",
                fontSize: "17px",

                marginBottom: "10px",
              }}
            >
              Call Summary
            </Typography>

            <Grid container spacing={1}>
              {/* Column 1 */}
              <Grid item xs={12} md={4}>
                <table
                  className="table mb-0 align-middle"
                  style={{ width: "100%" }}
                >
                  <tbody>
                    <tr>
                      <td className={classes.modalLabel}>Caller ID</td>
                      <td className={classes.modalValue}>
                        {selectedRow.caller_id_number || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.modalLabel}>DID Number</td>
                      <td className={classes.modalValue}>
                        {selectedRow.did_tfn || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.modalLabel}>Forwarded</td>
                      <td className={classes.modalValue}>
                        {selectedRow.forwarded_number || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.modalLabel}>Status</td>
                      <td className={classes.modalValue}>
                        {selectedRow.hangup_reason || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.modalLabel}>Call Status</td>
                      <td className={classes.modalValue}>
                        {selectedRow.call_result || "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Grid>

              {/* Column 2 */}
              <Grid item xs={12} md={4}>
                <table
                  className="table mb-0 align-middle"
                  style={{ width: "100%" }}
                >
                  <tbody>
                    <tr>
                      <td className={classes.modalLabel}>Buyer Name</td>
                      <td className={classes.modalValue}>
                        {selectedRow.buyer_name || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.modalLabel}>Cmp. Name</td>
                      <td className={classes.modalValue}>
                        {selectedRow.campaign_name || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.modalLabel}>Duration</td>
                      <td className={classes.modalValue}>
                        {selectedRow.duration || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.modalLabel}>Call Answer</td>
                      <td className={classes.modalValue}>
                        {formatDateTimeJSXModal(selectedRow.answer_at)}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.modalLabel}>Call Start</td>
                      <td className={classes.modalValue}>
                        {formatDateTimeJSXModal(selectedRow.start_at)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Grid>

              {/* Column 3 */}
              <Grid item xs={12} md={4}>
                <table
                  className="table mb-0 align-middle"
                  style={{ width: "100%" }}
                >
                  <tbody>
                    <tr>
                      <td className={classes.modalLabel}>Call End</td>
                      <td className={classes.modalValue}>
                        {formatDateTimeJSXModal(selectedRow.end_at)}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.modalLabel}>Hangup By</td>
                      <td className={classes.modalValue}>
                        {selectedRow.hangupby || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.modalLabel}>Unique ID</td>
                      <td className={classes.modalValue}>
                        {selectedRow.uniqueid || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.modalLabel}>Recording</td>
                      <td className={classes.modalValue}>
                        {selectedRow.recording_path ? (
                          <audio
                            ref={(audio) =>
                              audioControls.registerAudioRef(
                                selectedRow.recording_path,
                                audio,
                              )
                            }
                            src={selectedRow.recording_path}
                            controls
                            controlsList="download"
                            onPlay={() =>
                              audioControls.handleAudioClick(
                                selectedRow.recording_path,
                              )
                            }
                            onPause={audioControls.handleAudioPause}
                            className={classes.audioPlayer}
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Grid>
            </Grid>
          </Grid>

          {/* Buyer Dial Logs */}
          {/* <Grid item xs={12} sx={{ mt: 4 }}>
            <Typography
              sx={{
                fontWeight: "400",
                fontSize: "17px",
                marginBottom: "10px",
              }}
            >
              Buyer Dial Log
            </Typography>

            <div className={classes.tableContainer}>
              <table
                className="table mb-0 align-middle"
                style={{ width: "100%" }}
              >
                <thead className={classes.tableHeader}>
                  <tr>
                    {[
                      "Unique Id",
                      "Buyer Name",
                      "Buyer Number",
                      "Actual Timeout",
                      "Timeout",
                      "Status",
                      "Created At",
                    ].map((item, i) => (
                      <th
                        key={i}
                        style={{
                          fontSize: "12px",
                          color: "#0a140e",
                          whiteSpace: "nowrap",
                          padding: "8px",
                        }}
                      >
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {buyerDialogs?.length > 0 ? (
                    buyerDialogs.map((dialog, index) => (
                      <tr key={index}>
                        <td className={classes.tableCell}>{dialog.uniqueid}</td>
                        <td className={classes.tableCell}>
                          {dialog.buyer_name}
                        </td>
                        <td className={classes.tableCell}>
                          {dialog.buyer_number}
                        </td>
                        <td className={classes.tableCell}>
                          {dialog.actual_timeout}
                        </td>
                        <td className={classes.tableCell}>{dialog.timeout}</td>
                        <td className={classes.tableCell}>{dialog.status}</td>
                        <td className={classes.tableCell}>
                          {formatDateTimeJSXModal(dialog.created_at)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        No buyer dial logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Grid> */}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};